import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { BudgetRevenueAdminFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-revenue-admin/budget-revenue-admin-find.dto';
import type { BudgetRevenueAdminFindManyQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-revenue-admin/budget-revenue-admin-find-many-query.dto';
import { BasisBudgetRevenueAdminService } from '@zaoblako/web/core/services/basis/basis-budget-revenue-admin.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

@Component({
  selector: 'crm-budget-revenue-admin-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent, SafeHtmlPipe],
  templateUrl: './budget-revenue-admin-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetRevenueAdminSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public admins: BudgetRevenueAdminFindInterface[] = [];
  public queryParams: BudgetRevenueAdminFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedAdmin: BudgetRevenueAdminFindInterface;
  @Input() public set selectedAdmin(selectedAdmin: BudgetRevenueAdminFindInterface) {
    if (this.customSearch) {
      this._selectedAdmin = selectedAdmin;
      return;
    }

    if (this.admins.some((admin) => admin.id === selectedAdmin?.id)) {
      this._selectedAdmin = this.admins.find((admin) => admin.id === selectedAdmin.id);
      return;
    }

    if (selectedAdmin?.id) {
      this.basisBudgetRevenueAdminService.findById<BudgetRevenueAdminFindInterface>(selectedAdmin.id).subscribe({
        next: (_admin) => {
          if (!this.admins.some((admin) => admin.id === _admin.id)) {
            this.admins = this.admins.concat(_admin);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedAdmin = _admin;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedAdminChange = new EventEmitter<BudgetRevenueAdminFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedAdminId(id: string) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.admins.some((admin) => admin.id === id)) {
      this._selectedAdmin = this.admins.find((admin) => admin.id === id);
      return;
    }

    this.basisBudgetRevenueAdminService.findById<BudgetRevenueAdminFindInterface>(id).subscribe({
      next: (_admin) => {
        if (!this.admins.some((admin) => admin.id === _admin.id)) {
          this.admins = this.admins.concat(_admin);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedAdmin = _admin;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly basisBudgetRevenueAdminService: BasisBudgetRevenueAdminService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.basisBudgetRevenueAdminService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.admins = this.admins?.length
            ? this.admins.concat(data.items.filter((item) => !this.admins.some((admin) => admin.id === item.id)))
            : data.items.filter((item) => !this.admins.some((admin) => admin.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toRevenueAdmin(admin: any): BudgetRevenueAdminFindInterface {
    return admin as BudgetRevenueAdminFindInterface;
  }

  public onSelectChange(admin: BudgetRevenueAdminFindInterface): void {
    this.selectedAdminChange.emit(admin);
  }

  public onShowPanel(): void {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.admins.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.basisBudgetRevenueAdminService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.admins = this.admins.concat(
                    data.items.filter((item) => !this.admins.some((admin) => admin.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string): void {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.basisBudgetRevenueAdminService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedAdmin?.id) && this._selectedAdmin) {
            this.admins = data.items.concat(this._selectedAdmin);
          } else {
            this.admins = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(name: string): string {
    return HighlightMatchTextHelper.highlightMatchText(name, this.singleSelectComponent?.dropdown?.filterValue);
  }
}
